import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  maxHeight: "80vh",
  overflow: "auto",
};

export default function AssignTechnicianPopup({
  open,
  handleClose,
  technicianList,
  isForChange,
  handleAssignTechnician,
  handleChangeTechnician,
  isTechnicianAssigning,
  ticketDetailsForAssignTechnician,
}) {
  const [technicianSelected, setTechnicianSelected] = useState(null);

  const handleChange = (_, newValue) => {
    setTechnicianSelected(newValue);
  };

  // Reset selected technician when modal closes
  useEffect(() => {
    if (!open) {
      setTechnicianSelected(null);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!technicianSelected) {
      return;
    }

    if (isForChange) {
      handleChangeTechnician(technicianSelected._id);
    } else {
      handleAssignTechnician(technicianSelected._id);
    }
  };

  const getServiceInfo = () => {
    return {
      name:
        ticketDetailsForAssignTechnician?.service_provided_for?.service_name ||
        ticketDetailsForAssignTechnician?.service_provided_for?._id ||
        "Unknown Service",
      id: ticketDetailsForAssignTechnician?.service_provided_for?._id,
    };
  };

  const getLocationInfo = () => {
    return {
      pincode:
        ticketDetailsForAssignTechnician?.address_details?.pincode || "Unknown",
      city:
        ticketDetailsForAssignTechnician?.address_details?.city ||
        "Unknown City",
    };
  };

  const getCenterInfo = () => {
    return {
      name:
        ticketDetailsForAssignTechnician?.assigned_ids?.assigned_center_id
          ?.center_name || "Unknown Center",
      id: ticketDetailsForAssignTechnician?.assigned_ids?.assigned_center_id
        ?._id,
    };
  };

  const serviceInfo = getServiceInfo();
  const locationInfo = getLocationInfo();
  const centerInfo = getCenterInfo();

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography
          id="modal-modal-title"
          variant="h6"
          component="h2"
          gutterBottom
        >
          {isForChange ? "Change Technician" : "Assign Technician"}
        </Typography>

        {/* Ticket Information */}
        <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Request: {ticketDetailsForAssignTechnician?.ticket_id}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1 }}
            flexWrap="wrap"
            gap={1}
          >
            <Chip
              label={`Service: ${serviceInfo.name}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`Pincode: ${locationInfo.pincode}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
            <Chip
              label={`City: ${locationInfo.city}`}
              size="small"
              color="default"
              variant="outlined"
            />
            {isForChange && (
              <Chip
                label={`Center: ${centerInfo.name}`}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </Stack>

          {/* Information about technician selection */}
          <Box sx={{ display: "flex", alignItems: "flex-start", mt: 2 }}>
            <InfoIcon color="info" sx={{ fontSize: 16, mr: 1, mt: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              {isForChange
                ? `Showing technicians from assigned center: ${centerInfo.name}`
                : `Showing technicians for ${serviceInfo.name} service in pincode ${locationInfo.pincode}`}
            </Typography>
          </Box>

          {/* Warning if no technicians found */}
          {technicianList.length === 0 && (
            <Box sx={{ display: "flex", alignItems: "flex-start", mt: 1 }}>
              <WarningIcon
                color="warning"
                sx={{ fontSize: 16, mr: 1, mt: 0.5 }}
              />
              <Typography variant="caption" color="warning.main">
                No technicians found matching the criteria. Please check service
                availability or try different filters.
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Autocomplete
            value={technicianSelected}
            onChange={handleChange}
            options={technicianList}
            getOptionLabel={(option) =>
              `${option.name} (${option.pending_ticket_count || 0} pending)`
            }
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body1" fontWeight="medium">
                    {option.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    📞 {option.phone || "N/A"} | ⏳{" "}
                    {option.pending_ticket_count || 0} pending
                  </Typography>
                  {option.expertise && option.expertise.length > 0 && (
                    <Typography
                      variant="caption"
                      color="primary"
                      display="block"
                    >
                      Services:{" "}
                      {option.expertise
                        .map((s) => s.service_name || s)
                        .slice(0, 3)
                        .join(", ")}
                      {option.expertise.length > 3 &&
                        ` ...+${option.expertise.length - 3} more`}
                    </Typography>
                  )}
                  {option.pincodes && option.pincodes.length > 0 && (
                    <Typography
                      variant="caption"
                      color="secondary"
                      display="block"
                    >
                      Pincodes: {option.pincodes.slice(0, 3).join(", ")}
                      {option.pincodes.length > 3 &&
                        ` ...+${option.pincodes.length - 3} more`}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Choose Technician"
                fullWidth
                helperText={`Select from ${technicianList.length} available technicians`}
              />
            )}
          />
        </Box>

        <Box
          sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 3 }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isTechnicianAssigning}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!technicianSelected || isTechnicianAssigning}
            startIcon={
              isTechnicianAssigning ? <CircularProgress size={16} /> : null
            }
          >
            {isTechnicianAssigning
              ? "Processing..."
              : isForChange
              ? "Change Technician"
              : "Assign Technician"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
